import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, absoluteAssetUrl } from "../lib/api";
import { MODULE_STATUS_LABEL } from "../lib/moduleStatus";

const STAGES = [
    { id: 1, title: "The Source", tagline: "We start with the raw truth — your approved Work Instruction." },
    { id: 2, title: "AI Understanding", tagline: "Structure and metadata extracted, verbatim facts preserved." },
    { id: 3, title: "Human Verification", tagline: "Subject-matter experts approve every scene before it flows forward." },
    { id: 4, title: "AI Storyboard", tagline: "Sections become chapters; steps become scenes with type-aware framing." },
    { id: 5, title: "AI Trainer & Video", tagline: "A talking-head presenter plus cinematic B-roll, synced to verbatim narration." },
    { id: 6, title: "The Training Film", tagline: "Assembled, ducked, captioned, ready to publish and deploy." },
    { id: 7, title: "Assessment", tagline: "Coming in Cycle 2 — knowledge check with certificate." },
    { id: 8, title: "Ask AI", tagline: "Coming in Cycle 3 — grounded organisational knowledge assistant." },
];

export default function PresentDemo() {
    const { id } = useParams();
    const nav = useNavigate();
    const [stage, setStage] = useState(1);
    const [module, setModule] = useState(null);
    const [render, setRender] = useState(null);
    const [chapters, setChapters] = useState([]);
    const [heygenSigned, setHeygenSigned] = useState(null);
    const [runwaySigned, setRunwaySigned] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const [d, rs] = await Promise.all([
                api.get(`/modules/${id}`),
                api.get(`/modules/${id}/render-status`),
            ]);
            setModule(d.data);
            setRender(rs.data.latest_render || null);
            setChapters(d.data.chapters || []);
            // Load one HeyGen + one Runway sample for stage 5
            const scenesResp = await api.get(`/modules/${id}/versions/${d.data.module.current_version_id}/scenes`);
            const trainerScene = scenesResp.data.find((s) => s.selected_heygen_generation_id);
            const runwayScene = scenesResp.data.find((s) => s.selected_generation_id && s.scene_type === "B_RUNWAY_CINEMATIC");
            if (trainerScene) {
                const hg = await api.get(`/scenes/${trainerScene.id}/heygen-generations`);
                const sel = (hg.data || []).find((r) => r.is_selected) || hg.data?.[0];
                if (sel?.signed_url) setHeygenSigned(sel.signed_url);
            }
            if (runwayScene) {
                try {
                    const rw = await api.get(`/scenes/${runwayScene.id}/generations`);
                    const sel = (rw.data || []).find((r) => r.is_selected) || rw.data?.[0];
                    if (sel?.signed_url) setRunwaySigned(sel.signed_url);
                } catch { /* endpoint may differ */ }
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") nav(-1);
            else if (["ArrowRight", " ", "PageDown"].includes(e.key)) setStage((s) => Math.min(8, s + 1));
            else if (["ArrowLeft", "PageUp"].includes(e.key)) setStage((s) => Math.max(1, s - 1));
            else if (e.key === "s" || e.key === "S") setStage(6);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [nav]);

    // Plain-English label so the demo screen never leaks raw enums.

    if (loading || !module) {
        return <div className="fixed inset-0 bg-neutral-950 text-neutral-400 flex items-center justify-center">Loading…</div>;
    }

    const s = STAGES[stage - 1];
    const m = module.module;
    const isPreview = m.status !== "PUBLISHED";

    return (
        <div data-testid="present-demo" className="fixed inset-0 bg-neutral-950 text-neutral-100 overflow-hidden">
            {/* Header */}
            <div className="absolute top-0 inset-x-0 flex items-center justify-between px-8 py-5 z-10">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                    {module.organisation_name || "Acme Labs"} · presenting · {m.name}
                </div>
                {isPreview && <div className="text-[10px] font-medium uppercase tracking-widest text-amber-400" data-testid="demo-mode-watermark">DEMO MODE</div>}
                <button onClick={() => nav(-1)} data-testid="present-exit" className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-white">Exit · Esc</button>
            </div>

            {/* Content stage */}
            <div className="h-full w-full grid grid-cols-[100px,1fr] pt-24 pb-16 px-16">
                <div className="text-8xl font-thin text-neutral-800 self-start mono">{String(s.id).padStart(2, "0")}</div>
                <div className="pl-8 space-y-8 overflow-hidden">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">Stage {s.id} of 8</div>
                        <h1 className="mt-3 text-5xl font-medium tracking-tight">{s.title}</h1>
                        <p className="mt-2 text-lg text-neutral-400 max-w-2xl">{s.tagline}</p>
                    </div>

                    {/* Stage-specific content */}
                    {stage === 1 && (
                        <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-8 max-w-3xl">
                            <div className="text-xs uppercase tracking-widest text-neutral-500">Original Work Instruction</div>
                            <div className="mt-3 text-xl font-medium">{module.document_title || m.name}</div>
                            <div className="mono text-[11px] text-neutral-500 mt-1">{module.document_number || "—"} · Rev {module.document_revision || "—"} · Issued {module.document_issue_date || "—"}</div>
                            <div className="mt-4 text-sm text-neutral-400">Raw DOCX/PDF, parsed deterministically. Every number, every unit, every safety line preserved verbatim.</div>
                        </div>
                    )}
                    {stage === 2 && (
                        <div className="grid grid-cols-2 gap-6 max-w-5xl">
                            <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-6">
                                <div className="text-xs uppercase tracking-widest text-neutral-500">Extracted metadata</div>
                                <pre className="mono text-[11px] text-neutral-300 mt-3 whitespace-pre-wrap">{JSON.stringify({name: m.name, target_duration_seconds: m.target_duration_seconds, status: m.status, chapters: chapters.length}, null, 2)}</pre>
                            </div>
                            <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-6">
                                <div className="text-xs uppercase tracking-widest text-neutral-500">Chapters</div>
                                <ul className="mt-3 space-y-1 text-sm">
                                    {chapters.map((c) => <li key={c.id} className="flex items-center justify-between"><span>{c.name}</span><span className="mono text-[10px] text-neutral-500">{c.scene_count} scenes</span></li>)}
                                </ul>
                            </div>
                        </div>
                    )}
                    {stage === 3 && (
                        <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-8 max-w-3xl">
                            <div className="text-xs uppercase tracking-widest text-emerald-400">SME Approval on file</div>
                            <div className="mt-3 text-lg">Every scene reviewed. Every fact preserved.</div>
                            <div className="mt-3 text-[11px] text-neutral-500">Status: <span className="text-neutral-300" title={m.status} data-testid="present-demo-module-status">{MODULE_STATUS_LABEL[m.status] ?? m.status}</span></div>
                        </div>
                    )}
                    {stage === 4 && (
                        <div className="grid grid-cols-3 gap-4 max-w-6xl">
                            {chapters.slice(0, 5).map((c, i) => (
                                <div key={c.id} className="rounded-lg border border-neutral-800 bg-neutral-900 p-5"
                                     style={{ animationDelay: `${i * 150}ms` }}>
                                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Chapter {i + 1}</div>
                                    <div className="mt-1 text-base font-medium">{c.name}</div>
                                    <div className="mt-2 text-xs text-neutral-500">{c.scene_count} scenes · {c.total_duration_seconds}s</div>
                                </div>
                            ))}
                        </div>
                    )}
                    {stage === 5 && (
                        <div className="grid grid-cols-2 gap-6 max-w-6xl">
                            <div className="rounded-lg border border-neutral-800 overflow-hidden">
                                <div className="text-xs uppercase tracking-widest text-neutral-500 p-3 border-b border-neutral-800">Trainer video</div>
                                {heygenSigned ? <video src={absoluteAssetUrl(heygenSigned)} controls className="w-full aspect-video bg-black" /> : <div className="aspect-video bg-neutral-900 flex items-center justify-center text-neutral-500 text-sm">No trainer sample yet</div>}
                            </div>
                            <div className="rounded-lg border border-neutral-800 overflow-hidden">
                                <div className="text-xs uppercase tracking-widest text-neutral-500 p-3 border-b border-neutral-800">Cinematic B-roll</div>
                                {runwaySigned ? <video src={absoluteAssetUrl(runwaySigned)} controls className="w-full aspect-video bg-black" /> : <div className="aspect-video bg-neutral-900 flex items-center justify-center text-neutral-500 text-sm">No cinematic sample yet</div>}
                            </div>
                        </div>
                    )}
                    {stage === 6 && (
                        <div className="rounded-lg border border-neutral-800 overflow-hidden max-w-6xl">
                            {render?.signed_url ? (
                                <video src={absoluteAssetUrl(render.signed_url)} controls autoPlay className="w-full aspect-video bg-black" data-testid="present-final-video">
                                    {render.captions_signed_url && <track default kind="captions" srcLang="en" src={absoluteAssetUrl(render.captions_signed_url)} />}
                                </video>
                            ) : <div className="aspect-video bg-neutral-900 flex items-center justify-center text-neutral-500">No render yet</div>}
                        </div>
                    )}
                    {stage === 7 && (
                        <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-8 max-w-2xl">
                            <div className="text-xs uppercase tracking-widest text-amber-400">Cycle 2</div>
                            <div className="mt-2 text-2xl font-medium">Assessment & certification</div>
                            <p className="mt-2 text-sm text-neutral-400">Auto-generated knowledge checks from approved content. Signed completion certificates. Coming soon.</p>
                        </div>
                    )}
                    {stage === 8 && (
                        <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-8 max-w-2xl">
                            <div className="text-xs uppercase tracking-widest text-amber-400">Cycle 3</div>
                            <div className="mt-2 text-2xl font-medium">Ask AI</div>
                            <p className="mt-2 text-sm text-neutral-400">Grounded organisational knowledge — every answer traceable to an approved module or work instruction.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Progress dock */}
            <div className="absolute bottom-6 inset-x-0 flex items-center justify-center gap-4">
                {stage < 6 && <button onClick={() => setStage(6)} data-testid="present-skip-to-film" className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-white">Skip to film →</button>}
                <div className="flex items-center gap-1.5">
                    {STAGES.map((st) => (
                        <button key={st.id} onClick={() => setStage(st.id)} data-testid={`present-dot-${st.id}`}
                            className={`h-1.5 rounded-full transition-all ${stage === st.id ? "w-8 bg-white" : "w-1.5 bg-neutral-700 hover:bg-neutral-500"}`} />
                    ))}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-600">← / → to navigate</div>
            </div>
        </div>
    );
}
